import dayjs from 'dayjs';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import SecurityIcon from '@mui/icons-material/Security';

export const departmentConfigs = {
  fgi: {
    label: 'FGI',
    icon: AccountBalanceIcon,
    accent: '#12C1DC',
    typeLabel: 'Loan Type',
    description: 'Government loan deductions for SSS and Pag-IBIG repayment tracking.',
    scheduleLabel: 'Loan cycle',
    types: []
  },
  procurement: {
    label: 'Procurement',
    icon: InventoryIcon,
    accent: '#12C1DC',
    typeLabel: 'Deduction Item',
    description: 'Uniform, equipment, and asset recovery managed by procurement.',
    scheduleLabel: 'Recovery cycle',
    types: []
  },
  insurance: {
    label: 'Insurance',
    icon: SecurityIcon,
    accent: '#12C1DC',
    typeLabel: 'Coverage',
    description: 'Insurance premium recovery for employee-funded and optional plans.',
    scheduleLabel: 'Premium cycle',
    types: []
  }
};

export const statusPalette = {
  active: { label: 'Active', color: 'success' },
  pending: { label: 'Pending', color: 'warning' },
  completed: { label: 'Completed', color: 'default' },
  archived: { label: 'Archived', color: 'default' }
};

export const createEmptyForm = () => ({
  id: '',
  employeeName: '',
  employeeId: '',
  store: '',
  deductionType: '',
  procurementDeductionTypes: [],
  procurementOriginalTotalAmount: '',
  procurementTotalOverridden: false,
  frequency: 'Per cutoff',
  amount: '',
  totalAmount: '',
  paidInstallments: 0,
  totalInstallments: 1,
  firstDeductionDate: dayjs().format('YYYY-MM-DD'),
  lastDeductionDate: '',
  status: 'active',
  autoStop: true,
  remarks: ''
});

const toCents = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.max(0, Math.round(numericValue * 100)) : 0;
};

const fromCents = (value) => Number((Math.max(0, value) / 100).toFixed(2));

export const getNormalizedRecurringAmount = (totalAmount, amountPerRun) => {
  const totalAmountCents = toCents(totalAmount);
  const amountPerRunCents = toCents(amountPerRun);

  if (totalAmountCents <= 0 || amountPerRunCents <= 0) {
    return 0;
  }

  return fromCents(Math.min(totalAmountCents, amountPerRunCents));
};

export const calculateRecurringInstallmentCount = (totalAmount, amountPerRun, frequency = 'Per cutoff') => {
  if (frequency === 'One time') {
    return 1;
  }

  const totalAmountCents = toCents(totalAmount);
  const normalizedAmountCents = toCents(getNormalizedRecurringAmount(totalAmount, amountPerRun));

  if (totalAmountCents <= 0 || normalizedAmountCents <= 0) {
    return 0;
  }

  return Math.max(1, Math.ceil(totalAmountCents / normalizedAmountCents));
};

const buildMonthlyInstallmentDate = (baseDate, anchorDay) => {
  const nextDate = dayjs(baseDate).add(1, 'month').startOf('month');
  return nextDate.date(Math.min(anchorDay, nextDate.daysInMonth())).startOf('day');
};

const buildNextCutoffDate = (baseDate) => {
  const current = dayjs(baseDate).startOf('day');
  return current.date() <= 15 ? current.date(16) : current.add(1, 'month').startOf('month');
};

export const calculateDeductionLastDate = (firstDeductionDate, frequency, totalInstallments) => {
  const firstDate = dayjs(firstDeductionDate).startOf('day');
  const installmentCount = Math.max(Number(totalInstallments) || 0, 0);

  if (!firstDate.isValid() || installmentCount <= 0) {
    return '';
  }

  if (frequency === 'One time') {
    return firstDate.format('YYYY-MM-DD');
  }

  let cursor = firstDate;
  const anchorDay = firstDate.date();

  for (let installmentNumber = 1; installmentNumber < installmentCount; installmentNumber += 1) {
    cursor = frequency === 'Monthly'
      ? buildMonthlyInstallmentDate(cursor, anchorDay)
      : buildNextCutoffDate(cursor);
  }

  return cursor.format('YYYY-MM-DD');
};

export const buildInstallmentAmounts = ({ frequency, amount, totalAmount, totalInstallments }) => {
  const totalAmountCents = toCents(totalAmount || amount);
  if (totalAmountCents <= 0) {
    return [];
  }

  if (frequency === 'One time') {
    return [fromCents(totalAmountCents)];
  }

  const normalizedAmountCents = toCents(getNormalizedRecurringAmount(totalAmount, amount));
  const installmentCount = Math.max(
    Number(totalInstallments) || calculateRecurringInstallmentCount(totalAmount, amount, frequency),
    0
  );

  if (normalizedAmountCents <= 0 || installmentCount <= 0) {
    return [];
  }

  return Array.from({ length: installmentCount }, (_unusedValue, index) => {
    const isLastInstallment = index === installmentCount - 1;
    if (!isLastInstallment) {
      return fromCents(normalizedAmountCents);
    }

    const remainingAmountCents = totalAmountCents - (normalizedAmountCents * Math.max(installmentCount - 1, 0));
    return fromCents(remainingAmountCents > 0 ? remainingAmountCents : normalizedAmountCents);
  });
};

export const currencyFormatter = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2
});

export const formatDeductionDate = (value) => {
  if (!value) {
    return '-';
  }

  return dayjs(value).isValid() ? dayjs(value).format('MMM DD, YYYY') : value;
};

export const formatDeductionRange = (firstDeductionDate, lastDeductionDate) => {
  const formattedFirst = formatDeductionDate(firstDeductionDate);
  const formattedLast = formatDeductionDate(lastDeductionDate);

  if (!firstDeductionDate && !lastDeductionDate) {
    return '-';
  }

  if (!lastDeductionDate || firstDeductionDate === lastDeductionDate) {
    return formattedFirst;
  }

  return `${formattedFirst} - ${formattedLast}`;
};

export const buildEmployeeOptionsByStore = (stores = [], records = []) => {
  const directory = new Map();

  const addEmployee = (store, employeeName, employeeId) => {
    if (!store || !employeeName || !employeeId) {
      return;
    }

    if (!directory.has(store)) {
      directory.set(store, []);
    }

    const existingEmployees = directory.get(store);
    if (!existingEmployees.some((employee) => employee.employeeId === employeeId)) {
      existingEmployees.push({ employeeName, employeeId });
    }
  };

  records.forEach((record) => {
    addEmployee(record.store, record.employeeName, record.employeeId);
  });

  stores.forEach((store) => {
    if (!directory.has(store)) {
      directory.set(store, []);
    }
  });

  directory.forEach((employees) => {
    employees.sort((left, right) => left.employeeName.localeCompare(right.employeeName));
  });

  return Object.fromEntries(directory);
};
